import { useListActionsPageQueryControllerListPage } from "@/generated/follow-up-actions/follow-up-actions";
import type { FollowUpActionResponseDto } from "@/generated/model/followUpActionResponseDto";
import type { ListActionsPageQueryControllerListPageSortBy } from "@/generated/model/listActionsPageQueryControllerListPageSortBy";
import type { ListActionsPageQueryControllerListPageSortDir } from "@/generated/model/listActionsPageQueryControllerListPageSortDir";
import type { ListActionsPageQueryControllerListPageTab } from "@/generated/model/listActionsPageQueryControllerListPageTab";
import type { SortDir } from "@/lib/category-display";
import type {
  FollowUpActionSortColumn,
  FollowUpActionsTab,
} from "@/lib/follow-up-actions/follow-up-actions-list";

interface UseFollowUpActionsDataArgs {
  nurseryId?: string;
  tab: FollowUpActionsTab;
  categoryId?: string;
  search?: string;
  sortBy: FollowUpActionSortColumn;
  sortDir: SortDir;
  page: number;
  pageSize: number;
}

/**
 * Drives the Follow-up Actions list server-side: forwards tab, category,
 * search, sort and pagination to the paginated endpoint and reads back the
 * page plus the Open / Requires sign off count badges (which the server
 * computes with the same category/search filters, so they always match what
 * switching tabs would show).
 */
export function useFollowUpActionsData({
  nurseryId,
  tab,
  categoryId,
  search,
  sortBy,
  sortDir,
  page,
  pageSize,
}: UseFollowUpActionsDataArgs) {
  const enabled = !!nurseryId;

  const listQuery = useListActionsPageQueryControllerListPage(
    nurseryId ?? "",
    {
      tab: tab as ListActionsPageQueryControllerListPageTab,
      categoryId: categoryId || undefined,
      search: search || undefined,
      sortBy: sortBy as ListActionsPageQueryControllerListPageSortBy,
      sortDir: sortDir as ListActionsPageQueryControllerListPageSortDir,
      limit: pageSize,
      offset: page * pageSize,
    },
    // refetchOnMount "always": the global QueryClient defaults (staleTime 5min,
    // refetchOnMount false) would otherwise replay a cached page after an action
    // is signed off elsewhere — leaving a stale row in the wrong tab.
    { query: { enabled, refetchOnMount: "always" } },
  );

  const items: FollowUpActionResponseDto[] = listQuery.data?.data.data ?? [];
  const total: number = listQuery.data?.data.meta.total ?? 0;
  const counts = listQuery.data?.data.meta.counts ?? {
    open: 0,
    requiresSignOff: 0,
    history: 0,
  };

  return {
    items,
    total,
    isLoading: listQuery.isLoading,
    counts,
  };
}
